import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaSyncAlt, FaSignOutAlt } from 'react-icons/fa';
import { ModalMessage } from '../Components/ModalMessage/ModalMessage';
import { useAdminSubscriptions } from '../hooks/useAdminSubscriptions';
import './styling/AdminSubscriptions.css';

const PAGE_SIZE = 10;

// -------- Helpers de status (badge da tabela) --------
// O campo `status` já vem pronto do backend, um de:
// "ativo" | "inadimplente" | "expirado" | "cancelado" | "ativo (legado)" | "gratuito"
const STATUS_STYLES = {
    'ativo': { className: 'status-ativo', dot: '#4ade80' },
    'ativo (legado)': { className: 'status-legado', dot: '#5eead4' },
    'inadimplente': { className: 'status-inadimplente', dot: '#fb923c' },
    'expirado': { className: 'status-expirado', dot: '#ff6b6b' },
    'cancelado': { className: 'status-cancelado', dot: '#cbd5e1' },
    'gratuito': { className: 'status-gratuito', dot: '#93c5fd' },
};

const getStatusStyle = (status) => {
    const key = (status || '').toString().trim().toLowerCase();
    return STATUS_STYLES[key] || { className: 'status-cancelado', dot: '#cbd5e1' };
};

const StatusBadge = ({ status }) => {
    const { className, dot } = getStatusStyle(status);
    return (
        <span className={`status-badge ${className}`}>
            <span className="dot" style={{ backgroundColor: dot }} />
            {status || 'desconhecido'}
        </span>
    );
};

// -------- Breakdown de assinaturasPorStatus (chips do topo) --------
const STATUS_BREAKDOWN_META = {
    active: { label: 'Ativas', color: '#4ade80' },
    past_due: { label: 'Inadimplentes', color: '#fb923c' },
    expired: { label: 'Expiradas', color: '#ff6b6b' },
    canceled: { label: 'Canceladas', color: '#cbd5e1' },
    cancelled: { label: 'Canceladas', color: '#cbd5e1' },
    trialing: { label: 'Em teste', color: '#93c5fd' },
    incomplete: { label: 'Incompletas', color: '#9aa0a6' },
};

const getBreakdownMeta = (key) =>
    STATUS_BREAKDOWN_META[key] || { label: key, color: '#9aa0a6' };

// -------- Formatação --------
const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR');
};

const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

// Exibe o valor da transação como moeda BRL. Trata o `amount` como reais.
const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '—';
    const n = Number(amount);
    if (isNaN(n)) return String(amount);
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const StateBlock = ({ children, spinner }) => (
    <div className="admin-state">
        {spinner && <div className="admin-spinner" />}
        <span>{children}</span>
    </div>
);

const AdminSubscriptions = () => {
    const navigate = useNavigate();
    const {
        stats, statsLoading, statsError, fetchStats,
        subscribers, count, listLoading, listError, fetchSubscribers,
        detail, detailLoading, detailError, fetchUserDetail, clearDetail,
        expiryRunning, runExpiry,
    } = useAdminSubscriptions();

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [plan, setPlan] = useState(''); // '' = todos | 'pago' | 'gratuito'
    const [page, setPage] = useState(1);

    const [showExpiryConfirm, setShowExpiryConfirm] = useState(false);
    const [toast, setToast] = useState(null); // { type, title, message }
    const toastTimer = useRef(null);

    // Carrega stats uma vez.
    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // Debounce da busca (400ms).
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Reseta página quando muda busca ou filtro de plano.
    useEffect(() => {
        setPage(1);
    }, [search, plan]);

    // Busca a lista sempre que search/plan/page mudam.
    useEffect(() => {
        fetchSubscribers({ search, plan, page, size: PAGE_SIZE });
    }, [fetchSubscribers, search, plan, page]);

    const showToast = useCallback((type, title, message) => {
        setToast({ type, title, message });
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 6000);
    }, []);

    useEffect(() => () => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
    }, []);

    const handleRunExpiry = useCallback(async () => {
        setShowExpiryConfirm(false);
        try {
            const result = await runExpiry();
            showToast(
                'success',
                'Expiração concluída',
                `${result?.expired ?? 0} assinatura(s) expirada(s) · ${result?.usersRecomputed ?? 0} usuário(s) recalculado(s).`
            );
            // Recarrega dados para refletir os novos status.
            fetchStats();
            fetchSubscribers({ search, plan, page, size: PAGE_SIZE });
        } catch (err) {
            showToast('error', 'Erro ao rodar expiração', err.message || 'Tente novamente.');
        }
    }, [runExpiry, showToast, fetchStats, fetchSubscribers, search, plan, page]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

    // Janela de páginas (máx. 5 botões).
    const pageWindow = (() => {
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + 4);
        start = Math.max(1, end - 4);
        const arr = [];
        for (let p = start; p <= end; p++) arr.push(p);
        return arr;
    })();

    const rangeFrom = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeTo = Math.min(page * PAGE_SIZE, count);

    const planTabs = [
        { key: '', label: 'Todos' },
        { key: 'pago', label: 'Pagos' },
        { key: 'gratuito', label: 'Gratuitos' },
    ];

    const breakdownEntries = stats?.assinaturasPorStatus
        ? Object.entries(stats.assinaturasPorStatus).filter(([, v]) => Number(v) > 0)
        : [];

    return (
        <div className="admin-subs">
            {/* Header */}
            <header className="admin-subs-header">
                <div className="admin-subs-header-inner">
                    <div className="admin-subs-title-block">
                        <img
                            src="/imgs/reino_header_logo.svg"
                            alt="Reino Play"
                            className="admin-subs-logo"
                        />
                        <h1 className="admin-subs-title">
                            Usuários &amp; Assinaturas
                            <small>Painel de gestão de assinantes</small>
                        </h1>
                    </div>
                    <div className="admin-subs-actions">
                        <button className="admin-btn admin-btn-ghost" onClick={() => navigate('/manager')}>
                            <FaArrowLeft size={13} /> Dashboard
                        </button>
                        <button
                            className="admin-btn admin-btn-primary"
                            onClick={() => setShowExpiryConfirm(true)}
                            disabled={expiryRunning}
                        >
                            <FaSyncAlt size={13} className={expiryRunning ? 'spinning' : ''} />
                            {expiryRunning ? 'Rodando...' : 'Rodar expiração agora'}
                        </button>
                        <button className="admin-btn admin-btn-ghost" onClick={handleLogout} title="Sair">
                            <FaSignOutAlt size={14} />
                        </button>
                    </div>
                </div>
            </header>

            <main className="admin-subs-content">
                {/* Cards de resumo */}
                {statsError ? (
                    <div className="admin-error-box">{statsError}</div>
                ) : (
                    <>
                        <section className="admin-cards">
                            <div className="admin-card accent">
                                <span className="admin-card-label">Total de usuários</span>
                                <span className="admin-card-value">
                                    {statsLoading ? '—' : (stats?.total ?? 0)}
                                </span>
                            </div>
                            <div className="admin-card">
                                <span className="admin-card-label">Pagos</span>
                                <span className="admin-card-value">
                                    {statsLoading ? '—' : (stats?.pagos ?? 0)}
                                </span>
                                <span className="admin-card-hint">Assinantes com plano ativo</span>
                            </div>
                            <div className="admin-card">
                                <span className="admin-card-label">Gratuitos</span>
                                <span className="admin-card-value">
                                    {statsLoading ? '—' : (stats?.gratuitos ?? 0)}
                                </span>
                                <span className="admin-card-hint">Sem assinatura paga</span>
                            </div>
                        </section>

                        {/* Breakdown por status */}
                        {breakdownEntries.length > 0 && (
                            <div className="admin-status-breakdown">
                                {breakdownEntries.map(([key, value]) => {
                                    const meta = getBreakdownMeta(key);
                                    return (
                                        <div className="admin-status-chip" key={key}>
                                            <span className="dot" style={{ backgroundColor: meta.color }} />
                                            <span className="chip-count">{value}</span>
                                            <span className="chip-name">{meta.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {/* Toolbar: busca + filtro de plano */}
                <div className="admin-toolbar">
                    <div className="admin-search">
                        <FaSearch size={15} />
                        <input
                            type="text"
                            placeholder="Buscar por nome, email, CPF ou telefone..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </div>
                    <div className="admin-plan-filter">
                        {planTabs.map((t) => (
                            <button
                                key={t.key || 'todos'}
                                className={plan === t.key ? 'active' : ''}
                                onClick={() => setPlan(t.key)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tabela */}
                <div className="admin-table-wrap">
                    {listError ? (
                        <StateBlock>{listError}</StateBlock>
                    ) : listLoading ? (
                        <StateBlock spinner>Carregando usuários...</StateBlock>
                    ) : subscribers.length === 0 ? (
                        <StateBlock>Nenhum usuário encontrado.</StateBlock>
                    ) : (
                        <div className="admin-table-scroll">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Usuário</th>
                                        <th>Telefone</th>
                                        <th>Status</th>
                                        <th>Plano(s)</th>
                                        <th>Transações</th>
                                        <th>Cadastro</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscribers.map((u) => {
                                        const tags = (u.subscriptions && u.subscriptions.length > 0)
                                            ? u.subscriptions
                                            : (u.subscriptionRecords || []).map((r) => r.productId).filter(Boolean);
                                        return (
                                            <tr key={u.id} onClick={() => fetchUserDetail(u.id)}>
                                                <td>
                                                    <span className="admin-user-name">{u.name || '—'}</span>
                                                    <span className="admin-user-email">{u.email || '—'}</span>
                                                </td>
                                                <td className="admin-muted">{u.telefone || '—'}</td>
                                                <td><StatusBadge status={u.status} /></td>
                                                <td>
                                                    {tags.length > 0 ? (
                                                        <div className="admin-tags">
                                                            {tags.slice(0, 3).map((tag, i) => (
                                                                <span className="admin-tag" key={`${tag}-${i}`}>{tag}</span>
                                                            ))}
                                                            {tags.length > 3 && (
                                                                <span className="admin-tag">+{tags.length - 3}</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="admin-muted">—</span>
                                                    )}
                                                </td>
                                                <td className="admin-muted">{u.transactionsCount ?? 0}</td>
                                                <td className="admin-muted">{formatDate(u.createdAt)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Paginação */}
                    {!listLoading && !listError && count > 0 && (
                        <div className="admin-pagination">
                            <span className="admin-pagination-info">
                                Mostrando {rangeFrom}–{rangeTo} de {count}
                            </span>
                            <div className="admin-pagination-controls">
                                <button
                                    className="admin-page-btn"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                >
                                    ‹
                                </button>
                                {pageWindow[0] > 1 && (
                                    <>
                                        <button className="admin-page-btn" onClick={() => setPage(1)}>1</button>
                                        {pageWindow[0] > 2 && <span className="admin-muted">…</span>}
                                    </>
                                )}
                                {pageWindow.map((p) => (
                                    <button
                                        key={p}
                                        className={`admin-page-btn ${p === page ? 'active' : ''}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                {pageWindow[pageWindow.length - 1] < totalPages && (
                                    <>
                                        {pageWindow[pageWindow.length - 1] < totalPages - 1 && (
                                            <span className="admin-muted">…</span>
                                        )}
                                        <button className="admin-page-btn" onClick={() => setPage(totalPages)}>
                                            {totalPages}
                                        </button>
                                    </>
                                )}
                                <button
                                    className="admin-page-btn"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                >
                                    ›
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Modal / painel lateral de detalhe do usuário */}
            {(detail || detailLoading || detailError) && (
                <UserDetailDrawer
                    detail={detail}
                    loading={detailLoading}
                    error={detailError}
                    onClose={clearDetail}
                />
            )}

            {/* Confirmação da expiração */}
            {showExpiryConfirm && (
                <ModalMessage
                    title="Rodar expiração agora?"
                    description="Isso dispara a rede de segurança que expira assinaturas já vencidas e recalcula o status dos usuários. Deseja continuar?"
                    leftButtonText="Rodar agora"
                    leftButtonAction={handleRunExpiry}
                    rightButtonText="Cancelar"
                    rightButtonAction={() => setShowExpiryConfirm(false)}
                />
            )}

            {/* Toast de feedback */}
            {toast && (
                <div className={`admin-toast ${toast.type}`} onClick={() => setToast(null)}>
                    <strong>{toast.title}</strong>
                    <span>{toast.message}</span>
                </div>
            )}
        </div>
    );
};

// -------- Painel lateral de detalhe --------
const UserDetailDrawer = ({ detail, loading, error, onClose }) => {
    useEffect(() => {
        const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [onClose]);

    const records = detail?.subscriptionRecords || [];
    const transactions = detail?.transactions || [];

    return (
        <div
            className="admin-modal-overlay"
            onClick={(e) => { if (e.target.classList.contains('admin-modal-overlay')) onClose(); }}
        >
            <div className="admin-modal" role="dialog" aria-modal="true">
                <div className="admin-modal-header">
                    <div>
                        <h2>{loading ? 'Carregando...' : (detail?.name || 'Detalhe do usuário')}</h2>
                        {detail && <div className="sub">{detail.email}</div>}
                    </div>
                    <button className="admin-modal-close" onClick={onClose} aria-label="Fechar">&times;</button>
                </div>

                <div className="admin-modal-body">
                    {error ? (
                        <div className="admin-error-box">{error}</div>
                    ) : loading ? (
                        <StateBlock spinner>Carregando detalhes...</StateBlock>
                    ) : detail ? (
                        <>
                            {/* Dados */}
                            <div className="admin-detail-grid">
                                <div className="admin-detail-item">
                                    <div className="k">Status</div>
                                    <div className="v"><StatusBadge status={detail.status} /></div>
                                </div>
                                <div className="admin-detail-item">
                                    <div className="k">Perfil</div>
                                    <div className="v">{detail.role || '—'}</div>
                                </div>
                                <div className="admin-detail-item">
                                    <div className="k">CPF</div>
                                    <div className="v">{detail.cpf || '—'}</div>
                                </div>
                                <div className="admin-detail-item">
                                    <div className="k">Telefone</div>
                                    <div className="v">{detail.telefone || '—'}</div>
                                </div>
                                <div className="admin-detail-item">
                                    <div className="k">Cadastro</div>
                                    <div className="v">{formatDateTime(detail.createdAt)}</div>
                                </div>
                                <div className="admin-detail-item">
                                    <div className="k">Atualizado</div>
                                    <div className="v">{formatDateTime(detail.updatedAt)}</div>
                                </div>
                            </div>

                            {/* Assinaturas */}
                            <div className="admin-section-title">Assinaturas ({records.length})</div>
                            {records.length === 0 ? (
                                <div className="admin-empty-inline">Nenhuma assinatura registrada.</div>
                            ) : (
                                records.map((r, i) => (
                                    <div className="admin-sub-record" key={r.id || `${r.source}-${i}`}>
                                        <div className="admin-sub-record-top">
                                            <div>
                                                <div className="admin-sub-record-product">{r.productId || '—'}</div>
                                                <div className="admin-sub-record-source">{r.source || '—'}</div>
                                            </div>
                                            <StatusBadge status={r.status} />
                                        </div>
                                        <div className="admin-sub-record-meta">
                                            <span>Expira em: <b>{formatDate(r.currentPeriodEnd)}</b></span>
                                            {r.entitlements && (
                                                <span>
                                                    Acessos: <b>
                                                        {Array.isArray(r.entitlements)
                                                            ? (r.entitlements.join(', ') || '—')
                                                            : String(r.entitlements)}
                                                    </b>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}

                            {/* Histórico de transações */}
                            <div className="admin-section-title">
                                Histórico de transações ({transactions.length})
                            </div>
                            {transactions.length === 0 ? (
                                <div className="admin-empty-inline">Nenhuma transação registrada.</div>
                            ) : (
                                <div className="admin-table-scroll">
                                    <table className="admin-tx-table">
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Evento</th>
                                                <th>Origem</th>
                                                <th>Produto</th>
                                                <th>Valor</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.map((t, i) => (
                                                <tr key={t.id || i}>
                                                    <td className="admin-muted">{formatDateTime(t.createdAt)}</td>
                                                    <td className="admin-tx-event">{t.event || '—'}</td>
                                                    <td className="admin-muted">{t.source || '—'}</td>
                                                    <td>{t.productId || '—'}</td>
                                                    <td className="admin-tx-amount">{formatAmount(t.amount)}</td>
                                                    <td className="admin-muted">{t.status || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default AdminSubscriptions;
